import React, { useState, useEffect } from 'react';
import { documents } from '../utils/api';
import DocumentUploadForm from './Documents/DocumentUploadForm';

const BulkDocumentManager = ({ isOpen, onClose, selectedCoins, onUpdate }) => {
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [associatedDocuments, setAssociatedDocuments] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const docs = await documents.getAll();
        setAvailableDocuments(docs);
        
        // Get associated documents from the first coin's documents array
        // since we're doing bulk operations, we'll show what's common
        const coinDocs = docs.filter(doc => 
          doc.coin_ids?.some(id => selectedCoins.has(String(id)))
        );
        setAssociatedDocuments(new Set(coinDocs.map(doc => doc.id)));
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
        setError('Failed to load documents. Please try again later.');
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, selectedCoins]);

  const handleToggleDocument = async (doc) => {
    try {
      setUpdating(true);
      setError(null);
      const isAssociated = associatedDocuments.has(doc.id);
      
      // Prepare document update with all required fields
      const updatedDoc = {
        title: doc.title,
        typeId: doc.type_id,
        notes: doc.notes,
        coinIds: isAssociated
          ? (doc.coin_ids || []).filter(id => !selectedCoins.has(String(id)))
          : [...(doc.coin_ids || []), ...Array.from(selectedCoins)]
      };
      
      await documents.update(doc.id, updatedDoc);
      
      // Update local state
      setAssociatedDocuments(prev => {
        const next = new Set(prev);
        if (isAssociated) {
          next.delete(doc.id);
        } else {
          next.add(doc.id);
        }
        return next;
      });
      
      onUpdate();
    } catch (err) {
      console.error('Failed to update document associations:', err);
      setError('Failed to update document associations. Please try again later.');
    } finally {
      setUpdating(false);
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadForm(false);
    onUpdate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center w-full">
            <h3 className="text-lg font-medium text-gray-900">Manage Documents</h3>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowUploadForm(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Upload Document
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="text-red-600 text-center p-4">{error}</div>
        ) : (
          <div className="space-y-4 overflow-y-auto flex-1">
            <div className="text-sm text-gray-500 mb-4">
              {selectedCoins.size} coin{selectedCoins.size !== 1 ? 's' : ''} selected
            </div>
            
            {availableDocuments.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                No documents available
              </div>
            ) : (
              <div className="space-y-2">
                {availableDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="font-medium">{doc.title}</div>
                      <div className="text-sm text-gray-500">{doc.type_name}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleToggleDocument(doc)}
                        disabled={updating}
                        className={`px-3 py-1 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          associatedDocuments.has(doc.id)
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500'
                            : 'bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {associatedDocuments.has(doc.id) ? 'Remove' : 'Add'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>

      {/* Upload Form Modal */}
      <DocumentUploadForm
        open={showUploadForm}
        onClose={() => setShowUploadForm(false)}
        onSuccess={handleUploadSuccess}
        preSelectedCoins={selectedCoins}
      />
    </div>
  );
};

export default BulkDocumentManager;
